<script setup lang="ts">
import { RouterView } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { ref, computed, onMounted, onUnmounted } from 'vue'

const authStore = useAuthStore()
const isDropdownOpen = ref(false)
const isScrolled = ref(false)
const lastScrollY = ref(0)

const showNav = computed(() => authStore.isAuthenticated && !authStore.isCustomer)

const toggleDropdown = (event: Event) => {
  event.stopPropagation()
  isDropdownOpen.value = !isDropdownOpen.value
}

const handleLogout = () => {
  isDropdownOpen.value = false
  authStore.logout()
}

// Close dropdown when clicking outside
const closeDropdown = () => {
  isDropdownOpen.value = false
}

// Handle scroll events for bottom nav animation
const handleScroll = () => {
  const currentScrollY = window.scrollY
  
  // Show bottom nav when scrolling down, hide when scrolling up
  if (currentScrollY > lastScrollY.value && currentScrollY > 100) {
    // Scrolling down and past 100px threshold
    isScrolled.value = true
  } else if (currentScrollY < lastScrollY.value || currentScrollY <= 50) {
    // Scrolling up or near top of page
    isScrolled.value = false
  }
  
  lastScrollY.value = currentScrollY
}

// Throttle scroll events for better performance
let scrollTimeout: NodeJS.Timeout | null = null
const throttledHandleScroll = () => {
  if (scrollTimeout) return
  scrollTimeout = setTimeout(() => {
    handleScroll()
    scrollTimeout = null
  }, 50)
}

onMounted(() => {
  document.addEventListener('click', closeDropdown)
  window.addEventListener('scroll', throttledHandleScroll, { passive: true })
})

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown)
  window.removeEventListener('scroll', throttledHandleScroll)
  if (scrollTimeout) {
    clearTimeout(scrollTimeout)
  }
})
</script>

<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
    <!-- Top Navigation - Business & Account Details Only -->
    <nav v-if="showNav" class="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <!-- Logo Section - Left -->
          <div class="flex items-center space-x-2 sm:space-x-8">
            <div class="flex items-center space-x-2 sm:space-x-3">
              <div class="w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center shadow-md overflow-hidden">
                <img src="/kijani-logo.png" alt="Kijani Logo" class="w-full h-full object-contain">
              </div>
              <div class="flex flex-col">
                <h1 class="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                  Kijani Pantry
                </h1>
                <p class="text-xs text-gray-500 font-medium hidden sm:block">Business Platform</p>
              </div>
            </div>
          </div>

          <!-- User Section - Right -->
          <div class="relative" @click.stop>
            <button 
              @click="toggleDropdown"
              class="flex items-center space-x-1 sm:space-x-3 bg-gray-50 hover:bg-gray-100 px-2 sm:px-4 py-2 rounded-lg border border-gray-200 transition-colors duration-200"
            >
              <div class="text-right hidden sm:block">
                <p class="text-sm font-medium text-gray-900 leading-tight">
                  {{ authStore.user?.business_name || 'User' }}
                </p>
                <p class="text-xs text-gray-500 capitalize">
                  {{ (authStore.user?.role || 'user')?.replace('_', ' ') }}
                </p>
              </div>
              <div class="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center text-white font-semibold text-sm sm:text-base">
                {{ (authStore.user?.business_name?.charAt(0) || 'U').toUpperCase() }}
              </div>
              <svg 
                class="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 transition-transform duration-200 hidden sm:block" 
                :class="{ 'rotate-180': isDropdownOpen }" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>

            <!-- Dropdown Menu -->
            <transition
              enter-active-class="transition ease-out duration-200"
              enter-from-class="opacity-0 transform -translate-y-2"
              enter-to-class="opacity-100 transform translate-y-0"
              leave-active-class="transition ease-in duration-150"
              leave-from-class="opacity-100 transform translate-y-0"
              leave-to-class="opacity-0 transform -translate-y-2"
            >
              <div 
                v-if="isDropdownOpen"
                class="absolute right-0 mt-2 w-56 max-h-[70vh] overflow-y-auto bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50"
              >
                <p v-if="authStore.isAdminVendor" class="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  More Menu
                </p>
                <router-link
                  v-if="authStore.isAdminVendor"
                  to="/inventory"
                  @click="isDropdownOpen = false"
                  class="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-150"
                  :class="{ 'bg-blue-50 text-blue-600': $route.path === '/inventory' }"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                  </svg>
                  <span>Inventory</span>
                </router-link>
                <router-link
                  v-if="authStore.isAdminVendor"
                  to="/categories"
                  @click="isDropdownOpen = false"
                  class="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-150"
                  :class="{ 'bg-blue-50 text-blue-600': $route.path === '/categories' }"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
                  </svg>
                  <span>Categories</span>
                </router-link>
                <router-link
                  v-if="authStore.isAdminVendor"
                  to="/reports"
                  @click="isDropdownOpen = false"
                  class="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-150"
                  :class="{ 'bg-blue-50 text-blue-600': $route.path === '/reports' }"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                  </svg>
                  <span>Reports</span>
                </router-link>
                <router-link
                  to="/orders"
                  @click="isDropdownOpen = false"
                  class="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-150"
                  :class="{ 'bg-blue-50 text-blue-600': $route.path === '/orders' }"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                  </svg>
                  <span>Orders</span>
                </router-link>
                <router-link
                  v-if="authStore.isAdminVendor"
                  to="/notifications"
                  @click="isDropdownOpen = false"
                  class="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-150"
                  :class="{ 'bg-blue-50 text-blue-600': $route.path === '/notifications' }"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-5 5v-5zM9 7H4l5-5v5zM20 4a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2h16z"/>
                  </svg>
                  <span>Notifications</span>
                </router-link>
                <router-link
                  v-if="authStore.isAdminVendor"
                  to="/settings"
                  @click="isDropdownOpen = false"
                  class="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                  :class="{ 'bg-gray-50 text-gray-900': $route.path === '/settings' }"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                  <span>Settings</span>
                </router-link>
                <div v-if="authStore.isAdminVendor" class="border-t border-gray-100 my-1"></div>
                <button
                  @click="handleLogout"
                  class="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                  </svg>
                  <span>Sign Out</span>
                </button>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </nav>

    <!-- Main Content -->
    <main class="pb-20 sm:pb-24"> <!-- Responsive padding-bottom to accommodate bottom nav -->
      <RouterView />
    </main>

    <!-- Bottom Navigation - Admin Portal -->
    <nav 
      v-if="showNav && authStore.isAdminVendor" 
      class="fixed bottom-0 left-0 right-0 z-40 sm:bottom-6 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2 sm:w-max sm:max-w-[calc(100vw-1rem)] transition-all duration-500 ease-out"
      :class="{
        'sm:opacity-0 sm:translate-y-10 sm:pointer-events-none': !isScrolled,
        'sm:opacity-100 sm:translate-y-0': isScrolled
      }"
    >
      <div class="bg-white/95 backdrop-blur-lg shadow-[0_-2px_10px_rgba(0,0,0,0.08)] sm:shadow-2xl border-t border-gray-200/60 sm:border sm:rounded-2xl px-1 sm:px-4 py-1.5 sm:py-3">
        <div class="flex items-center justify-around gap-0.5 overflow-x-auto sm:justify-start sm:space-x-1 sm:overflow-visible">
          <router-link 
            to="/dashboard" 
            class="flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 min-w-[3rem] sm:min-w-16 group"
            active-class="text-blue-600 bg-blue-50/80 shadow-sm"
          >
            <svg class="w-4 h-4 sm:w-5 sm:h-5 mb-0.5 sm:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2zM3 7h18M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7"/>
            </svg>
            <span class="text-xs font-medium leading-tight">Dashboard</span>
          </router-link>

          <router-link 
            to="/inventory" 
            class="hidden sm:flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 min-w-12 sm:min-w-16 group"
            active-class="text-blue-600 bg-blue-50/80 shadow-sm"
          >
            <svg class="w-4 h-4 sm:w-5 sm:h-5 mb-0.5 sm:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
            </svg>
            <span class="text-xs font-medium leading-tight">Inventory</span>
          </router-link>

          <router-link 
            to="/products" 
            class="flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 min-w-[3rem] sm:min-w-16 group"
            active-class="text-blue-600 bg-blue-50/80 shadow-sm"
          >
            <svg class="w-4 h-4 sm:w-5 sm:h-5 mb-0.5 sm:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
            </svg>
            <span class="text-xs font-medium leading-tight">Products</span>
          </router-link>

          <router-link 
            to="/categories" 
            class="hidden sm:flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 min-w-12 sm:min-w-16 group"
            active-class="text-blue-600 bg-blue-50/80 shadow-sm"
          >
            <svg class="w-4 h-4 sm:w-5 sm:h-5 mb-0.5 sm:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
            </svg>
            <span class="text-xs font-medium leading-tight">Categories</span>
          </router-link>

          <router-link 
            to="/reports" 
            class="hidden sm:flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 min-w-12 sm:min-w-16 group"
            active-class="text-blue-600 bg-blue-50/80 shadow-sm"
          >
            <svg class="w-4 h-4 sm:w-5 sm:h-5 mb-0.5 sm:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            </svg>
            <span class="text-xs font-medium leading-tight">Reports</span>
          </router-link>

          <router-link 
            to="/customers" 
            class="flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 min-w-[3rem] sm:min-w-16 group"
            active-class="text-blue-600 bg-blue-50/80 shadow-sm"
          >
            <svg class="w-4 h-4 sm:w-5 sm:h-5 mb-0.5 sm:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
            </svg>
            <span class="text-xs font-medium leading-tight">Customers</span>
          </router-link>

          <router-link 
            to="/payments" 
            class="flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 transition-all duration-200 min-w-[3rem] sm:min-w-16 group"
            active-class="text-emerald-600 bg-emerald-50/80 shadow-sm"
          >
            <svg class="w-4 h-4 sm:w-5 sm:h-5 mb-0.5 sm:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a5 5 0 00-10 0v2m-2 0h14a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2v-7a2 2 0 012-2z"/>
            </svg>
            <span class="text-xs font-medium leading-tight">Payments</span>
          </router-link>

          <router-link 
            to="/pos" 
            class="flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-600 hover:text-green-600 hover:bg-green-50 transition-all duration-200 min-w-[3rem] sm:min-w-16 group"
            active-class="text-green-600 bg-green-50/80 shadow-sm"
          >
            <svg class="w-4 h-4 sm:w-5 sm:h-5 mb-0.5 sm:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
            </svg>
            <span class="text-xs font-medium leading-tight">POS</span>
          </router-link>
        </div>
      </div>
    </nav>

    <!-- Bottom Navigation - Sales Vendor Portal -->
    <nav 
      v-if="showNav && authStore.isSalesVendor" 
      class="fixed bottom-3 sm:bottom-6 left-1/2 transform -translate-x-1/2 z-50 w-max max-w-[calc(100vw-1rem)] transition-all duration-500 ease-out"
      :class="{
        'opacity-0 translate-y-10 pointer-events-none': !isScrolled,
        'opacity-100 translate-y-0': isScrolled
      }"
    >
      <div class="bg-white/95 backdrop-blur-lg shadow-2xl rounded-xl sm:rounded-2xl border border-gray-200/60 px-2 sm:px-4 py-2 sm:py-3">
        <div class="flex items-center space-x-0.5 sm:space-x-1">
          <router-link 
            to="/orders" 
            class="flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 min-w-12 sm:min-w-16 group"
            active-class="text-blue-600 bg-blue-50/80 shadow-sm"
          >
            <svg class="w-4 h-4 sm:w-5 sm:h-5 mb-0.5 sm:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
            </svg>
            <span class="text-xs font-medium leading-tight">Orders</span>
          </router-link>

          <router-link 
            to="/customers" 
            class="flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 min-w-12 sm:min-w-16 group"
            active-class="text-blue-600 bg-blue-50/80 shadow-sm"
          >
            <svg class="w-4 h-4 sm:w-5 sm:h-5 mb-0.5 sm:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
            </svg>
            <span class="text-xs font-medium leading-tight">Customers</span>
          </router-link>

          <router-link 
            to="/pos" 
            class="flex flex-col items-center p-2 sm:p-3 rounded-lg sm:rounded-xl text-gray-600 hover:text-green-600 hover:bg-green-50 transition-all duration-200 min-w-12 sm:min-w-16 group"
            active-class="text-green-600 bg-green-50/80 shadow-sm"
          >
            <svg class="w-4 h-4 sm:w-5 sm:h-5 mb-0.5 sm:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
            </svg>
            <span class="text-xs font-medium leading-tight">POS</span>
          </router-link>
        </div>
      </div>
    </nav>
  </div>
</template>

<style scoped>
/* Smooth transitions for all elements */
* {
  transition: all 0.2s ease-in-out;
}

/* Custom shadow for the floating bottom nav */
.fixed nav {
  box-shadow: 
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04),
    0 0 0 1px rgba(255, 255, 255, 0.9) inset;
}

/* Hover effect for bottom nav items */
.router-link-active {
  position: relative;
}

.router-link-active::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  background: currentColor;
  border-radius: 50%;
}

/* Ensure smooth scrolling behavior */
html {
  scroll-behavior: smooth;
}
</style>
